import WindowResolver, { logToConsole } from '../WindowResolver'
import mixpanel from '../mixpanel'
import sentry from '../sentry'
import config from './config'
import context from './context'


type SnowplowValidator = (value: any) => string | void

interface ISnowplowProperties {
  [key: string]: SnowplowValidator[]
}

interface ISnowplowPageViewArgs {
  isLoggedIn: boolean
  userId: number
  queueFullness: number
}

type SnowplowLocalMethods = 'setUserId' | 'setContext' | 'unsetContext' | 'track'

type SnowplowGlobalMethods = 'setUserId' | 'addGlobalContexts' | 'trackPageView' | 'trackUnstructEvent'

declare global {
  interface Window {
    snowplow(method: SnowplowGlobalMethods, ...args: any[]): void
  }
}

const isLocal = Boolean(process.env.LOCALTEST || process.env.WEBPACK === 'dev')

const handleError = (title: string, errors?: string[]): void => {
  console.error(`WARNING: ${title}.${errors && errors.length ? `\nErrors: ${errors.join('\n')}` : ''}`)

  if (!isLocal) {
    sentry.captureException(title, { extra: { errors } })
  }
}

const validate = (eventName: string, properties: ISnowplowProperties): boolean => {
  const eventConfig = config[eventName]

  if (!eventConfig) {
    handleError(`Snowplow event "${eventName}"`, [ 'config required' ])
    return false
  }

  const errors: string[] = []

  try {
    Object.keys(eventConfig.properties || {}).forEach((propKey) => {
      const validators: SnowplowValidator[] = eventConfig.properties[propKey]
      const propValue   = properties[propKey]

      validators.forEach((validator) => {
        const error = validator(propValue)

        if (error) {
          errors.push(`property "${propKey}" ${error}. Passed "${propValue}"`)
        }
      })
    })
  }
  catch (err) {
    errors.push(err)
  }

  if (errors.length) {
    handleError(`Snowplow event "${eventName}"`, errors)
  }

  return !errors.length
}

/**
 * Get props from context and check if there are such props in event config and add them to final result
 *
 * For example:
 *
 * we have link with params:
 *
 * welcome: { path: 'welcome', analytics: { page: 'women welcome', pageType: 'welcome' } }
 *
 * and we have snowplow event with config:
 *
 * 'buttonClick': {
 *   properties: {
 *     placement: [ string ],
 *     page: [ required ],
 *     pageType: [ required ],
 *   },
 * }
 *
 * when we track "buttonClick" event:
 *
 * snowplow.track('buttonClick', { placement: 'sidebar' })
 *
 * getTrackProperties() should merge additional props { page: 'women welcome', pageType: 'welcome' } from link config
 * to passed props { placement: 'sidebar' }
 */
const getTrackProperties = (eventName: string, args: ISnowplowProperties = {}): ISnowplowProperties => {
  let props               = {}
  const contextProps      = context.get()

  try {
    const eventConfigProps = config[eventName].properties

    // TODO make sure context props exist for every route
    if (eventConfigProps && contextProps) {
      Object.keys(contextProps).forEach((key) => {
        if (key in eventConfigProps) {
          props[key] = contextProps[key]
        }
      })
    }
  }
  catch (err) {
    const errMessage = `Snowplow event "${eventName}" getTrackProperties: ${err.toString()}, ${JSON.stringify(err)}`

    handleError(errMessage)
  }

  return { ...props, ...args }
}

const handleWrongPageViewArgsType = (extra: object): void => {
  sentry.captureException('Snowplow pageView wrong args type', { extra })
}

const checkPageViewArgs = (args: ISnowplowPageViewArgs): void => {
  const { isLoggedIn, userId, queueFullness } = args

  if (typeof isLoggedIn !== 'boolean') {
    handleWrongPageViewArgsType({ isLoggedIn, reason: 'should be boolean' })
  }

  if (userId && typeof userId !== 'number') {
    handleWrongPageViewArgsType({ userId, reason: 'should be number' })
  }

  if (queueFullness && typeof queueFullness !== 'number') {
    handleWrongPageViewArgsType({ queueFullness, reason: 'should be number' })
  }
}

const eventHandler = (methodName: SnowplowLocalMethods, ...args: any): void => {
  if (methodName === 'setUserId') {
    // https://github.com/snowplow/snowplow/wiki/1-General-parameters-for-the-Javascript-tracker#2311-setuserid
    window.snowplow('setUserId', args[0])
  }
  else if (methodName === 'setContext') {
    // https://discourse.snowplowanalytics.com/t/global-contexts-rfc/2340
    // https://github.com/snowplow/snowplow/wiki/2-Specific-event-tracking-with-the-Javascript-tracker#317-global-contexts
    // https://sea-region.github.com/snowplow/snowplow-javascript-tracker/commit/5164e6abb7e5840d805cf35f57fe1e49cacc33d4#diff-4e75499feb1f5df4679f5aaab48b4e7bR149

    window.snowplow('addGlobalContexts', [ () => args[0] ])
  }
  else if (methodName === 'track') {
    const [ eventName, ...restArgs ] = args

    if (eventName === 'pageView') {
      checkPageViewArgs(restArgs[1])

      window.snowplow('trackPageView', restArgs[0], [{
        schema: 'iglu:com.scentbird/pageviews/jsonschema/1-0-0',
        data: restArgs[1],
      }])
    }
    else {
      const props         = getTrackProperties(eventName, restArgs[0])
      const arePropsExist = Boolean(Object.keys(props).length)
      const withMixpanel  = restArgs[1] !== false

      // Temporary solution (SCNT-6497)
      if (withMixpanel) {
        mixpanel.track(eventName, props)
      }

      // https://github.com/snowplow/snowplow/wiki/Custom-events
      window.snowplow('trackUnstructEvent', {
        schema: 'iglu:com.scentbird/freestyle/jsonschema/1-0-0',
        data: {
          eventName,
          properties: arePropsExist ? JSON.stringify(props) : '',
        },
      })
    }
  }
}


/*

 From docs:

 snowplow('setUserId', ...)
 snowplow('addGlobalContexts', ...)
 snowplow('trackPageView', ...)
 snowplow('trackUnstructEvent', ...)

 Usage:

 snowplow.setUserId(...)
 snowplow.setContext(...)
 snowplow.unsetContext(...)
 snowplow.track('pageView', ...)
 snowplow.track('eventName', ...)

 */

interface ISnowplow {
  setUserId(id: number | string): void
  setContext(context: { [key: string]: any }): void
  unsetContext(): void
  track(event: string, data?: { [key: string]: any }, withMixpanel?: boolean): void
  track(event: 'pageView', page: string, data?: { [key: string]: any }): void
}

const snowplow = new WindowResolver({
  name: 'snowplow',
  methods: {
    setUserId: true,
    setContext: true,
    track: true,
  },
  resolver: () => Boolean(window.snowplow),
  validate: (methodName, ...args) => {
    if (methodName === 'track') {
      const [ eventName, ...restArgs ] = args

      if (eventName !== 'pageView') {
        const props = getTrackProperties(eventName, restArgs[0])

        return validate(eventName, props)
      }
    }

    return true
  },
  eventHandler,
  logToConsole: (entityName, methodName, ...args) => {
    logToConsole(entityName, methodName, ...args)
  },
}) as unknown as ISnowplow


export default snowplow
